import React from 'react';
import { Upload, FileText, X, Type, AlertCircle, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import * as pdfjsLib from 'pdfjs-dist';

if (typeof window !== 'undefined' && 'Worker' in window) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
  }
}

interface FileUploadFieldProps {
  fieldId: string;
  value: string | undefined;
  acceptedFileTypes?: string;
  maxFileSizeMB?: number;
  placeholder?: string;
  isRequired: boolean;
  onValueChange: (value: string) => void;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  fieldId,
  value,
  acceptedFileTypes = '.pdf,.doc,.docx,.txt',
  maxFileSizeMB = 10,
  placeholder,
  isRequired,
  onValueChange,
}) => {
  const [showTextInput, setShowTextInput] = React.useState(false);
  const [manualText, setManualText] = React.useState('');
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [fileError, setFileError] = React.useState<string>('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [processingMessage, setProcessingMessage] = React.useState('');
  const [isDragOver, setIsDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    setProcessingMessage('Reading PDF file...');
    const arrayBuffer = await file.arrayBuffer();

    setProcessingMessage('Loading PDF document...');
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
    });

    const pdf = await loadingTask.promise;
    const totalPages = pdf.numPages;

    if (totalPages === 0) {
      throw new Error('PDF has no pages');
    }

    let fullText = '';
    for (let i = 1; i <= totalPages; i++) {
      setProcessingMessage(`Extracting text from page ${i} of ${totalPages}...`);
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => ('str' in item ? item.str : ''))
        .filter((str: string) => str.trim().length > 0)
        .join(' ');

      if (pageText.trim()) {
        fullText += pageText + '\n\n';
      }
    }

    if (!fullText.trim()) {
      throw new Error('No text content found in PDF. The document may be scanned or image-based. Please use the "Paste Text" option instead.');
    }

    return fullText.trim();
  };

  const extractTextFromTxt = async (file: File): Promise<string> => {
    return await file.text();
  };

  const processFile = async (file: File) => {
    setFileError('');
    setIsProcessing(true);

    const maxBytes = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setFileError(`File size exceeds ${maxFileSizeMB}MB limit.`);
      setIsProcessing(false);
      return;
    }

    try {
      let text = '';
      const ext = file.name.split('.').pop()?.toLowerCase();

      if (ext === 'pdf') {
        text = await extractTextFromPDF(file);
      } else if (ext === 'txt') {
        text = await extractTextFromTxt(file);
      } else if (ext === 'doc' || ext === 'docx') {
        setProcessingMessage('Processing Word document...');
        try {
          const mammoth = await import('mammoth');
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          text = result.value;
        } catch {
          throw new Error('Failed to process Word document. Please save as PDF or paste the text directly.');
        }
      } else {
        throw new Error('Unsupported file type. Please upload a PDF, Word (.doc/.docx), or text (.txt) file.');
      }

      if (!text.trim()) {
        throw new Error('No text could be extracted from this file. Please try a different file or paste the text directly.');
      }

      setFileName(file.name);
      setProcessingMessage('');
      onValueChange(text);
    } catch (err: any) {
      setFileError(err.message || 'Failed to process file.');
      setFileName(null);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (e.target) e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleTextSubmit = () => {
    if (manualText.trim()) {
      setFileName(null);
      onValueChange(manualText.trim());
    }
  };

  const handleClear = () => {
    setFileName(null);
    setManualText('');
    setFileError('');
    onValueChange('');
  };

  const hasContent = !!value;
  const wordCount = value ? value.split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="space-y-4">
      {!showTextInput ? (
        <>
          {!hasContent ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              className={clsx(
                'relative flex flex-col items-center justify-center w-full min-h-[200px] border-2 border-dashed rounded-2xl transition-all duration-300 cursor-pointer',
                isDragOver
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50/50',
                isProcessing && 'pointer-events-none opacity-70'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileChange}
                className="hidden"
              />

              {isProcessing ? (
                <div className="flex flex-col items-center gap-3 px-6 py-4">
                  <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
                  <p className="text-sm font-medium text-gray-700">{processingMessage || 'Processing...'}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 px-6 py-4">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                    <Upload className="w-6 h-6 text-gray-500" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-medium text-gray-700">
                      Drop your document here or <span className="text-gray-900 underline">browse</span>
                    </p>
                    <p className="text-sm text-gray-400 mt-1">PDF, Word (.doc/.docx), or Text (.txt) up to {maxFileSizeMB}MB</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    {fileName && <p className="text-sm font-medium text-gray-900">{fileName}</p>}
                    <p className="text-xs text-gray-500">{wordCount.toLocaleString()} words extracted</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3 max-h-32 overflow-y-auto">
                <p className="text-xs text-gray-500 whitespace-pre-wrap line-clamp-6">
                  {value?.substring(0, 500)}{value && value.length > 500 ? '...' : ''}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-gray-900" />
                <span className="text-sm font-medium text-gray-900">Document uploaded successfully</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <textarea
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder={placeholder || 'Paste the full text of your existing document here...'}
            rows={8}
            className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 text-gray-900 text-sm placeholder-gray-400 resize-y"
          />
          <button
            type="button"
            onClick={handleTextSubmit}
            disabled={!manualText.trim()}
            className="inline-flex items-center px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4 mr-2" />
            Use This Text
          </button>
          {hasContent && (
            <div className="flex items-center gap-2 text-sm text-gray-900">
              <Check className="w-4 h-4" />
              <span className="font-medium">Text submitted ({wordCount.toLocaleString()} words)</span>
            </div>
          )}
        </div>
      )}

      {fileError && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-100">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{fileError}</span>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setShowTextInput(!showTextInput);
          setFileError('');
        }}
        className="inline-flex items-center gap-2 text-sm text-gray-900 hover:text-black font-medium transition-colors"
      >
        {showTextInput ? (
          <>
            <Upload className="w-4 h-4" />
            Upload a file instead
          </>
        ) : (
          <>
            <Type className="w-4 h-4" />
            Paste text instead
          </>
        )}
      </button>
    </div>
  );
};
